import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import styles from './BannerManagement.module.scss';
import { useMessageError, useMessageSuccess } from '../../../hooks/message.hook';
import { AuthContext } from '../../../context/AuthContext';
import BurgerMenu from '../../../components/BurgerMenu/BurgerMenu';

const BannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    imageUrl: '',
    categoryId: '',
    active: true,
    priority: 1
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const messageError = useMessageError();
  const messageSuccess = useMessageSuccess();
  const auth = useContext(AuthContext);

  const config = {
    headers: auth.isAuthenticated ? { Authorization: `Bearer ${auth.token}` } : {},
  };

  useEffect(() => {
    fetchBanners();
    fetchCategories();
  }, []);

  const fetchBanners = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('/api/admin/banners', config);
      setBanners(response.data.data);
      messageSuccess('Банери успішно завантажено');
    } catch (error) {
      messageError('Помилка при завантаженні банерів');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/admin/categories/getall', config);
      setCategories(response.data.categories || response.data.data || response.data);
    } catch (error) {
      console.error('Помилка завантаження категорій:', error);
      messageError('Помилка при завантаженні категорій');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      imageUrl: '',
      categoryId: '',
      active: true,
      priority: 1
    });
    setSelectedFile(null);
    setImagePreview(null);
    setEditingBanner(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('description', formData.description);
      submitData.append('categoryId', formData.categoryId);
      submitData.append('active', formData.active);
      submitData.append('priority', formData.priority);
      
      if (!editingBanner && formData.imageUrl) {
        submitData.append('imageUrl', formData.imageUrl);
      }
      
      if (selectedFile) {
        submitData.append('file', selectedFile);
      }

      if (editingBanner) {
        await axios.put(`/api/admin/banners/${editingBanner._id}`, submitData, {
          ...config,
          headers: {
            ...config.headers,
            'Content-Type': 'multipart/form-data',
          },
        });
        messageSuccess('Банер успішно оновлено');
      } else {
        await axios.post('/api/admin/banners', submitData, {
          ...config,
          headers: {
            ...config.headers,
            'Content-Type': 'multipart/form-data',
          },
        });
        messageSuccess('Банер успішно створено');
      }
      
      fetchBanners();
      resetForm();
    } catch (error) {
      console.error('Помилка відправки банера:', error);
      messageError(editingBanner ? 'Помилка оновлення банера' : 'Помилка створення банера');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (banner) => {
    setFormData({
      title: banner.title,
      description: banner.description,
      imageUrl: banner.imageUrl,
      categoryId: banner.categoryId._id,
      active: banner.active,
      priority: banner.priority
    });
    setImagePreview(banner.imageUrl);
    setEditingBanner(banner);
    setShowForm(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const handleDelete = async (bannerId) => {
    if (!window.confirm('Ви впевнені, що хочете видалити цей банер?')) {
      return;
    }

    setIsLoading(true);
    try {
      await axios.delete(`/api/admin/banners/${bannerId}`, config);
      setBanners(banners.filter(banner => banner._id !== bannerId));
      messageSuccess('Банер успішно видалено');
    } catch (error) {
      messageError('Помилка видалення банера');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleActive = async (banner) => {
    setIsLoading(true);
    try {
      const updatedBanner = { ...banner, active: !banner.active };
      await axios.put(`/api/admin/banners/${banner._id}`, updatedBanner, config);
      fetchBanners();
      messageSuccess(`Банер ${updatedBanner.active ? 'активовано' : 'деактивовано'}`);
    } catch (error) {
      messageError('Помилка зміни статусу банера');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.bannerManagement}>
      <BurgerMenu />
      
      <div className={styles.header}>
        <h1 className={styles.title}>
          <span className={styles.icon}>🎯</span>
          Управління банерами
        </h1>
        <button 
          className={styles.addButton}
          onClick={() => setShowForm(!showForm)}
        >
          <span className={styles.buttonIcon}>➕</span>
          {showForm ? 'Скасувати' : 'Додати банер'}
        </button>
      </div>

      {showForm && (
        <div className={styles.formSection}>
          <h2 className={styles.formTitle}>
            {editingBanner ? 'Редагувати банер' : 'Створити новий банер'}
          </h2>
          
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Назва банера *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className={styles.input}
                placeholder="Введіть назву банера"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Опис</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className={styles.textarea}
                placeholder="Опис банера (необов'язково)"
                rows="3"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Зображення банера *</label>
              {imagePreview && (
                <div className={styles.imagePreview}>
                  <img src={imagePreview} alt="Preview" className={styles.previewImage} />
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className={styles.fileInput}
                required={!editingBanner && !formData.imageUrl}
              />
              <p className={styles.fileInputHelper}>
                Оберіть зображення для банера (JPG, PNG, WebP)
              </p>
            </div>

            {!selectedFile && !editingBanner && (
              <div className={styles.formGroup}>
                <label className={styles.label}>Або введіть URL зображення</label>
                <input
                  type="url"
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                  className={styles.input}
                  placeholder="https://example.com/image.jpg"
                />
              </div>
            )}

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Категорія *</label>
                <select
                  value={formData.categoryId}
                  onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                  className={styles.select}
                  required
                >
                  <option value="">Оберіть категорію</option>
                  {categories.map((category) => (
                    <option key={category._id} value={category._id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.label}>Пріоритет</label>
                <input
                  type="number"
                  min="1"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 1 })}
                  className={styles.input}
                  placeholder="1"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className={styles.checkbox}
                />
                <span className={styles.checkboxCustom}></span>
                Активний банер
              </label>
            </div>

            <div className={styles.formActions}>
              <button 
                type="button" 
                onClick={resetForm}
                className={styles.cancelButton}
              >
                Скасувати
              </button>
              <button 
                type="submit" 
                disabled={isLoading}
                className={styles.submitButton}
              >
                {isLoading ? 'Збереження...' : (editingBanner ? 'Оновити' : 'Створити')}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className={styles.bannersGrid}>
        {isLoading && <div className={styles.loader}>Завантаження...</div>}
        
        {banners.length === 0 && !isLoading ? (
          <div className={styles.emptyState}>
            <h3>Немає банерів</h3>
            <p>Створіть свій перший банер для каруселі</p>
          </div>
        ) : (
          banners.map((banner) => (
            <div key={banner._id} className={`${styles.bannerCard} ${!banner.active ? styles.inactive : ''}`}>
              <div className={styles.bannerImage}>
                <img src={banner.imageUrl} alt={banner.title} />
                <div className={styles.bannerOverlay}>
                  <button 
                    onClick={() => toggleActive(banner)}
                    className={`${styles.statusButton} ${banner.active ? styles.active : styles.inactive}`}
                  >
                    {banner.active ? '✓' : '✕'}
                  </button>
                </div>
              </div>
              
              <div className={styles.bannerContent}>
                <h3 className={styles.bannerTitle}>{banner.title}</h3>
                {banner.description && (
                  <p className={styles.bannerDescription}>{banner.description}</p>
                )}
                
                <div className={styles.bannerMeta}>
                  <span className={styles.category}>
                    📂 {banner.categoryId?.name || 'Немає категорії'}
                  </span>
                  <span className={styles.priority}>
                    ⭐ Пріоритет: {banner.priority}
                  </span>
                  {banner.clickCount > 0 && (
                    <span className={styles.clicks}>
                      👆 Кліків: {banner.clickCount}
                    </span>
                  )}
                </div>

                <div className={styles.bannerActions}>
                  <button 
                    onClick={() => handleEdit(banner)}
                    className={styles.editButton}
                  >
                    ✏️ Редагувати
                  </button>
                  <button 
                    onClick={() => handleDelete(banner._id)}
                    className={styles.deleteButton}
                  >
                    🗑️ Видалити
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BannerManagement;